import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, Filter, X } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { clients } from '../../mocks/reportsData';

export const ReportsFilterBar = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [dateRange, setDateRange] = useState({ from: '', to: '' });

    // Load initial params
    const client = searchParams.get('client') || 'all';
    const period = searchParams.get('period') || 'month';
    const from = searchParams.get('from') || '';
    const to = searchParams.get('to') || '';

    useEffect(() => {
        if (from && to) setDateRange({ from, to });
    }, [from, to]);

    const updateParams = (key, value) => {
        const newParams = new URLSearchParams(searchParams);
        if (value && value !== 'all') {
            newParams.set(key, value);
        } else {
            newParams.delete(key);
        }

        // Reset custom date params if period changes from custom
        if (key === 'period' && value !== 'custom') {
            newParams.delete('from');
            newParams.delete('to');
        }

        setSearchParams(newParams);
    };

    const handleCustomDateSubmit = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('period', 'custom');
        if (dateRange.from) newParams.set('from', dateRange.from);
        if (dateRange.to) newParams.set('to', dateRange.to);
        setSearchParams(newParams);
    };

    return (
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                <Filter className="h-4 w-4" />
                Filtros de Reporte:
            </div>

            <div className="flex flex-col md:flex-row gap-4 flex-1 w-full md:w-auto">
                {/* Client Selector */}
                <div className="w-full md:w-64">
                    <Select value={client} onValueChange={(v) => updateParams('client', v)}>
                        <SelectTrigger>
                            <SelectValue placeholder="Seleccionar Cliente" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos los Clientes</SelectItem>
                            {clients.map(c => (
                                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Period Selector */}
                <div className="w-full md:w-48">
                    <Select value={period} onValueChange={(v) => updateParams('period', v)}>
                        <SelectTrigger>
                            <Calendar className="mr-2 h-4 w-4" />
                            <SelectValue placeholder="Periodo" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="month">Este Mes</SelectItem>
                            <SelectItem value="quarter">Últimos 3 Meses</SelectItem>
                            <SelectItem value="half-year">Últimos 6 Meses</SelectItem>
                            <SelectItem value="year">Este Año</SelectItem>
                            <SelectItem value="custom">Personalizado</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Custom Period Inputs */}
                {period === 'custom' && (
                    <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-4 duration-300">
                        <Input
                            type="date"
                            className="w-36"
                            value={dateRange.from}
                            onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                        />
                        <span className="text-gray-400">-</span>
                        <Input
                            type="date"
                            className="w-36"
                            value={dateRange.to}
                            onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                        />
                        <Button size="sm" variant="ghost" onClick={handleCustomDateSubmit}>Aplicar</Button>
                    </div>
                )}
            </div>

            {/* Clear Filters */}
            {(client !== 'all' || period !== 'month') && (
                <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-500 hover:text-red-600"
                    onClick={() => setSearchParams({})}
                >
                    <X className="h-4 w-4 mr-1" />
                    Limpiar
                </Button>
            )}
        </div>
    );
};
